import type { EnemyType, TowerType } from '../core/types';
import { ENEMY_NAMES, TOWER_NAMES } from './displayNames';
import { SHOP_ORDER, menuTowerTexture } from './unlocks';

/**
 * The avatar icons a player may wear on the leaderboard.
 *
 * A CLOSED set of art the game already ships. There is no upload, no free text
 * and no custom name — a player picks one of the pictures below or the neutral
 * default. That is what keeps the avatar out of the personal-information
 * argument in docs/superpowers/specs/2026-09-12-privacy-compliance-design.md
 * §14: the icon says which tower somebody likes, which is a preference of the
 * same character as a score, not a fact about a child in the world.
 *
 * `set_avatar` validates against this list server-side; a client cannot write
 * `profiles.avatar` directly. `tests/unit/avatarSqlMirror.test.ts` asserts the
 * SQL copy of the list has not drifted from this one.
 *
 * Spec: docs/superpowers/specs/2026-09-20-leaderboards-design.md §2
 */

export type AvatarGroup = 'tower' | 'currency' | 'enemy' | 'shop';

export interface AvatarIcon {
  /**
   * Stored in `profiles.avatar`. For the free icons this is also the texture
   * key, as it always was — every row already saved keeps rendering. Shop
   * icons use `icon-<code>` instead, because three of them cannot be named
   * after their art: two share a frame with another icon, and one's name says
   * tier 4 while its art is tier 3. See `texture`.
   */
  key: string;
  /**
   * The Phaser texture to draw. Split from `key` for the Avatar Shop (spec
   * §2): Upside-Downy and the free Hambi icon are both `tower-patty-1`, and
   * the title logo is a wide banner rather than a square sprite.
   */
  texture: string;
  /** Shown under the icon in the picker. */
  label: string;
  group: AvatarGroup;
  /** Towers only: the roster entry that unlocks this icon. */
  tower?: TowerType;
  /** Shop icons only: the `/give … icon <code>` name, and the price in coins. */
  code?: string;
  price?: number;
  /** Draw the texture flipped vertically (Upside-Downy). */
  flipY?: boolean;
}

/** Stored when a player has never chosen — drawn as a neutral silhouette. */
export const DEFAULT_AVATAR = '';

/**
 * Enemies offered as icons. A curated handful rather than all 45: the picker is
 * a grid a child scrolls, and the roster towers already fill most of it. These
 * are the bosses and the mascots players actually recognise.
 */
export const AVATAR_ENEMY_TYPES: readonly EnemyType[] = [
  'grunt',
  'dino',
  'fry',
  'hotdog',
  'frost',
  'boss',
  'nullnugget',
  'ultranugget',
  'kingnuggetron',
  'kingember',
  'shrekbruit',
  'chiply',
  'microwave',
  'nacho',
  'princenacho',
  'lifeguard',
];

/** Where a bought icon is recorded — a list of codes, synced like any progress. */
export const AVATAR_ICONS_KEY = 'hambi.avatarIcons';

/**
 * The Avatar Shop's eight icons, in shelf order (spec §2).
 *
 * Seven reuse art the game already ships; only the crown was drawn for the
 * shop, through the normal generator. Upside-Downy is `tower-patty-1` with
 * `flipY` rather than a second sprite — the same trick `SPRITE_FACING` uses
 * to mirror a charging Hambi, and the reason there is no third art pipeline.
 *
 * The Shrekbook uses tier 3 deliberately: Shrekbook Hambi has three tiers, so
 * tier 3 IS its final form. There is no `tower-shrekbook-4` to point at.
 */
export const SHOP_AVATARS: readonly AvatarIcon[] = [
  { code: 'patty4', label: 'Hambi Tier 4', texture: 'tower-patty-4', price: 250 },
  { code: 'generic', label: 'The Hambi Generic', texture: 'splash-title', price: 100 },
  { code: 'ninja4', label: 'Ninja Awakened', texture: 'tower-ninja-4', price: 300 },
  { code: 'crown', label: 'The Crown', texture: 'icon-crown', price: 150 },
  { code: 'shrekbook2', label: 'The Shrekbook', texture: 'tower-shrekbook-3', price: 500 },
  {
    code: 'upsidedown',
    label: 'Upside-Downy',
    texture: 'tower-patty-1',
    price: 200,
    flipY: true,
  },
  { code: 'takeout', label: 'Takeout', texture: 'takeout-normal', price: 400 },
  { code: 'wiseness', label: 'Ultra-Wiseness', texture: 'tower-wise-4', price: 450 },
].map((i) => ({ ...i, key: `icon-${i.code}`, group: 'shop' as const }));

/** Just the codes — what `/give … icon` takes and what the save stores. */
export const SHOP_AVATAR_CODES: readonly string[] = SHOP_AVATARS.map((i) => i.code!);

/**
 * Every icon, in picker order: the towers in shop order first (so the grid
 * reads like the Shop), then the two currencies, then the enemies, then the
 * bought ones last — a player scrolls past what they have to what they earned.
 */
export const AVATAR_ICONS: readonly AvatarIcon[] = [
  ...SHOP_ORDER.map(
    (tower): AvatarIcon => ({
      // Tier-1 frame: every tower has one, and it is the silhouette players
      // know from the shop shelf.
      key: `tower-${tower}-1`,
      // The Golden Shroom shows GOLD everywhere off the board (MENU_TIER).
      texture: menuTowerTexture(tower),
      label: TOWER_NAMES[tower],
      group: 'tower',
      tower,
    }),
  ),
  { key: 'coin', texture: 'coin', label: 'Hambi Coin', group: 'currency' },
  { key: 'capycoin', texture: 'capycoin', label: 'Capycoin', group: 'currency' },
  ...AVATAR_ENEMY_TYPES.map(
    (enemy): AvatarIcon => ({
      key: `enemy-${enemy}`,
      texture: `enemy-${enemy}`,
      label: ENEMY_NAMES[enemy],
      group: 'enemy',
    }),
  ),
  ...SHOP_AVATARS,
];

export const AVATAR_KEYS: readonly string[] = AVATAR_ICONS.map((i) => i.key);

const KEY_SET = new Set(AVATAR_KEYS);
const BY_KEY = new Map(AVATAR_ICONS.map((i) => [i.key, i]));

/** Is this one of the shipped icons? The default ('') is deliberately NOT. */
export function isAvatarKey(key: string): boolean {
  return KEY_SET.has(key);
}

export function avatarIcon(key: string): AvatarIcon | undefined {
  return BY_KEY.get(key);
}

/**
 * A stored value from the server, made safe to render. Anything unrecognised —
 * an icon removed in a later update, a tampered row — becomes the default
 * rather than a missing-texture box.
 */
export function normalizeAvatar(raw: string | null | undefined): string {
  return typeof raw === 'string' && isAvatarKey(raw) ? raw : DEFAULT_AVATAR;
}

/** Is this one of the eight Avatar Shop codes? */
export function isShopAvatarCode(code: unknown): code is string {
  return typeof code === 'string' && SHOP_AVATAR_CODES.includes(code);
}

/** The shop icon a `/give … icon <code>` names, or undefined. */
export function shopAvatar(code: string): AvatarIcon | undefined {
  return SHOP_AVATARS.find((i) => i.code === code);
}

/**
 * Tower icons need the tower; shop icons need to have been bought; everything
 * else is always available.
 *
 * `roster` is whatever `availableTowers(entered, unlockedExtras)` returned —
 * the same roster the Equip tray and the tower guide use. `owned` is the codes
 * in `hambi.avatarIcons`.
 */
export function avatarUnlocked(
  icon: AvatarIcon,
  roster: readonly TowerType[],
  owned: readonly string[] = [],
): boolean {
  if (icon.group === 'shop') return owned.includes(icon.code!);
  return icon.tower === undefined || roster.includes(icon.tower);
}

/** Keys the player may equip right now, in picker order. */
export function unlockedAvatarKeys(
  roster: readonly TowerType[],
  owned: readonly string[] = [],
): string[] {
  return AVATAR_ICONS.filter((i) => avatarUnlocked(i, roster, owned)).map((i) => i.key);
}
